import React, { useState, useRef } from "react";
import Link from "@/assets/icons/LinkBlack.svg";
import VerticalMenu from "@/assets/icons/VerticalMenu.svg";
import PlaySm from "@/assets/icons/PlaySm.svg";
import ImagePlaceholder from "@/assets/ImagePlaceholder.svg";
import { showCustomToast } from "./CustomToast";
import { useNavigate } from "react-router-dom";
import { AnimatePresence } from "motion/react";
import ReportOptions from "./Reports/ReportOptions";
import ConfirmDeleteReport from "./Reports/ConfirmDeleteReport";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { profilePictures } from "@/lib/data";
import useUserData from "@/hooks/useUserData";
import { useWorkspaceStore } from "@/hooks/useWorkspace";

const Report = ({
  report,
  onDeleteSuccess,
}: {
  report: any;
  onDeleteSuccess?: any;
}) => {
  const [showOptions, setShowOptions] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const issueOptionsRef = useRef(null);
  const navigate = useNavigate();

  const { user } = useUserData();
  const { currentWorkspace } = useWorkspaceStore();

  useOutsideClick(issueOptionsRef, () => {
    setShowOptions(false);
  });

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 60) {
      if (diffInMinutes === 0) return "Just now";
      if (diffInMinutes === 1) return "1 minute ago";
      return `${diffInMinutes} minutes ago`;
    }

    if (diffInHours < 24) {
      if (diffInHours === 1) return "1 hour ago";
      return `${diffInHours} hours ago`;
    }

    if (diffInDays < 7) {
      if (diffInDays === 1) return "1 day ago";
      return `${diffInDays} days ago`;
    }

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    // const hours = String(date.getHours()).padStart(2, "0");
    // const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${day}.${month}.${year}`;
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds || !isFinite(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <>
      <AnimatePresence>
        {showConfirmDelete && (
          <ConfirmDeleteReport
            reportId={report._id}
            setShowConfirmDelete={setShowConfirmDelete}
            onDeleteSuccess={onDeleteSuccess}
          />
        )}
      </AnimatePresence>
      <div
        onClick={() => {
          navigate(`/media?id=${report._id}`);
        }}
        className="relative cursor-pointer overflow-hidden rounded-[20px] w-full h-[200px] border-[1px] border-white/[0.08]"
      >
        <div className="absolute z-[50] w-[calc(100%-24px)] left-[12px] p-1 bottom-[12px] text-gray-900 font-medium text-[14px] leading-[20px]">
          <p className="truncate mb-2">{report.title}</p>
          <div className="flex justify-between items-center">
            <div className="flex gap-2 items-center">
              {/* <ProfilePicture pictureKey={report.user?.profilePicture} /> */}
              {report.user?.profilePicture ? (
                <img
                  src={profilePictures[report.user.profilePicture - 1]}
                  className="border-[1px] w-[20px] h-[20px] border-white/[0.32] rounded-[50%]"
                />
              ) : (
                <div className="border-[1px] w-[21px] h-[21px] border-white/[0.55] rounded-[50%]" />
              )}
              {report?.user?.username}
            </div>
            <p className="text-white/[0.48]">
              {formatTimestamp(report.createdAt)}
            </p>
          </div>
        </div>
        <div
          style={{
            background:
              "linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgb(0, 0, 0) 100%)",
          }}
          className="pointer-events-none absolute top-0 left-0 w-full h-full z-[10]"
        />

        <div className="absolute z-[11] top-[12px] right-[12px] flex gap-2 items-center">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard.writeText(
                `https://app.usetool.bar/media?id=${report._id}`,
              );
              showCustomToast("Link copied to clipboard", "check");
            }}
            className="w-[30px] z-[11] h-[30px] bg-white rounded-[50%] flex items-center justify-center"
          >
            <img src={Link} />
          </button>

          {currentWorkspace?.users
            .map((user) => user._id)
            .includes(user?._id) && (
            <div ref={issueOptionsRef} className="relative">
              <AnimatePresence>
                {showOptions && (
                  <ReportOptions
                    setShowConfirmDelete={setShowConfirmDelete}
                    setShowOptions={setShowOptions}
                  />
                )}
              </AnimatePresence>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowOptions((prev) => !prev);
                }}
                className="w-[30px] h-[30px] bg-gray-400/[0.4] backdrop-blur-md rounded-[50%] flex items-center justify-center"
              >
                <img src={VerticalMenu} />
              </button>
            </div>
          )}
        </div>

        {report?.mediaType === "video" && report.videoDuration && (
          <div className="z-[11] h-[30px] absolute top-[12px] left-[12px] bg-gray-50/[0.72] backdrop-blur-md rounded-[37px] flex items-center gap-1.5 justify-center px-3">
            <img src={PlaySm} />
            <p className="text-white text-sm font-medium">
              {formatDuration(report.videoDuration)}
            </p>
          </div>
        )}

        {report?.mediaType === "image" && (
          <img
            className="hover:scale-105 transition-all absolute object-center object-cover top-0 left-0 w-full h-full z-[1]"
            src={report.media[0]}
            alt="Issue media"
          />
        )}

        {report?.mediaType === "video" && report.thumbnail && (
          <img
            className="hover:scale-105 transition-all absolute object-center object-cover top-0 left-0 w-full h-full z-[1]"
            src={report.thumbnail}
            alt="Video thumbnail"
          />
        )}

        {report?.mediaType === "comments" && report.media?.[0] && (
          <img
            className="hover:scale-105 transition-all absolute object-center object-cover top-0 left-0 w-full h-full z-[1]"
            src={report.media[0]}
            alt="Comments thumbnail"
          />
        )}

        {(report?.mediaType === "dom-replay" ||
          (report?.mediaType === "video" && !report.thumbnail)) && (
          <div className="hover:scale-105 transition-all flex flex-col h-full items-center justify-center gap-[8px]">
            <img src={ImagePlaceholder} className="w-[76px] h-[76px]" />
            <div>
              <p className="text-white text-base leading-[24px]">
                No preview for this issue
              </p>
              <p className="mt-1 w-[175px] text-[14px] text-gray-800 text-center leading-[20px]">
                Enter the issue to see more details
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Report;
